import { useState, useEffect, useCallback } from 'react'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import { getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'

export const useFollowups = (bookedEventId, actionTrack, enabled = true) => {
  const [followups, setFollowups] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [totalElements, setTotalElements] = useState(0)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const fetchFollowups = useCallback(async () => {
    if (!enabled || !bookedEventId || !actionTrack) {
      setFollowups([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const params = {
        bookedEventId,
        actionTrack,
        page,
        size,
        sort: 'followupAt,desc',
      }

      const response = await getSPDataWithParams('/api/secure/cc/support/followups', params)

      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)?.['support-followups']
        setFollowups(data?.content || [])
        setTotalElements(data?.totalElements || 0)
        setPage(data?.pageable?.pageNumber || 0)
      } else {
        setError('Failed to fetch followups')
        setFollowups([])
      }
    } catch (err) {
      console.error('Error fetching followups:', err)
      setError('An error occurred while fetching followups')
      setFollowups([])
    } finally {
      setLoading(false)
    }
  }, [bookedEventId, actionTrack, page, size, enabled])

  useEffect(() => {
    fetchFollowups()
  }, [fetchFollowups])

  const addFollowup = useCallback(
    async (followupData) => {
      setLoading(true)
      setError(null)

      try {
        const payload = {
          bookedEventId,
          actionTrack,
          note: followupData.note,
          followupMode: followupData.followupMode || 'manual',
          status: followupData.status || 'done',
          ...(followupData.followupAt && { followupAt: followupData.followupAt }),
          ...(followupData.nextFollowupAt && { nextFollowupAt: followupData.nextFollowupAt }),
          ...(followupData.eventTriggerActionId && { eventTriggerActionId: followupData.eventTriggerActionId }),
          ...(followupData.stepKey && { stepKey: followupData.stepKey }),
        }

        const response = await postSPDataWithParams('/api/secure/cc/support/followups', payload)

        if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
          // Refresh the list
          await fetchFollowups()
          return { success: true }
        } else {
          setError('Failed to add followup')
          return { success: false, error: 'Failed to add followup' }
        }
      } catch (err) {
        console.error('Error adding followup:', err)
        setError('An error occurred while adding followup')
        return { success: false, error: err.message }
      } finally {
        setLoading(false)
      }
    },
    [bookedEventId, actionTrack, fetchFollowups],
  )

  return {
    followups,
    loading,
    error,
    totalElements,
    page,
    size,
    setPage,
    addFollowup,
    refreshFollowups: fetchFollowups,
  }
}

